import { useSelector } from 'react-redux';
import { shallowEqual } from 'react-redux';
// Market data selectors moved to centralized selectors
import { getUserState } from '../Redux/Reducers/userReducer';
import { 
  selectFunds,
  selectLots, 
  selectTrades, 
  selectProcessedTrades, 
  selectDeletes, 
  selectManagers, 
  selectAccountingData,
  selectCustodians,
  selectBrokers,
  selectSecuritiesLoaded,
  selectReports,
  selectReportPopupInfo,
  selectAccountingFile,
  selectFile,
  selectHistoricalSignalSet,
  selectBlacklistSignal,
  selectBlacklistedTickers,
  selectStrategies,
  selectRoutes,
  selectTickers,
  selectLoading,
  selectLoadingMsg,
  selectIsLoading,
  selectActiveRequests,
  selectShowMarketLoader,
  selectWebsocketDisconnected,
  selectIsStrategist,
  selectIsInvestor,
  selectMarketData,
  selectRawMarketData,
  selectSignalsPerformance,
  selectLivePrices,
  selectMarkets,
  getMarketDataThreshold,
  selectSwapLoading,
  selectSwapStrategies,
  selectSwapStrategiesAvailableDate,
  selectAllTrades,
  selectWebsocketManualReconnect,
} from '../Redux/Reducers/selectors';

// Custom hook for App component state
export const useAppState = () => {
  return useSelector((state) => ({
    user: getUserState(state),
    managers: selectManagers(state),
    socketapiData: state?.users?.apiInfo,
    socketuser: state?.users?.userInfo,
    loadingmsg: selectLoadingMsg(state),
    loading: selectLoading(state),
    isLoading: selectIsLoading(state),
    req: selectActiveRequests(state),
    showMarketLoader: selectShowMarketLoader(state),
    websocketDisconnected: selectWebsocketDisconnected(state),
    websocketManualReconnect: selectWebsocketManualReconnect(state)
  }), shallowEqual);
};

// Custom hook for state (most commonly used across components) - OPTIMIZED
export const useTodosState = () => {
  return useSelector((state) => ({
    tickers: selectTickers(state),
    lots: selectLots(state),
    trades: selectTrades(state),
    processedTrades: selectProcessedTrades(state),
    deletes: selectDeletes(state),
    accountingData: selectAccountingData(state),
    managers: selectManagers(state),
    strategies: selectStrategies(state),
    custodians: selectCustodians(state),
    brokers: selectBrokers(state),
    securitiesLoaded: selectSecuritiesLoaded(state),
    reports: selectReports(state),
    reportPopupInfo: selectReportPopupInfo(state),
    accountingFile: selectAccountingFile(state),
    routes: selectRoutes(state),
    file: selectFile(state),
    marketData: selectMarketData(state),
    RawMarketData: selectRawMarketData(state),
    signalsPerformance: selectSignalsPerformance(state),
    livePrices: selectLivePrices(state),
    markets: selectMarkets(state),
    isStrategist: selectIsStrategist(state),
    funds: selectFunds(state),
  }), shallowEqual);
};

// Custom hook for user state
export const useUserState = () => {
  return useSelector((state) => ({
    user: state?.users?.user,
    userInfo: state?.users?.userInfo,
    apiInfo: state?.users?.apiInfo
  }), shallowEqual);
};

// Custom hook for market data threshold
export const useMarketDataThreshold = () => {
  return useSelector(getMarketDataThreshold);
};



// Custom hook specifically for VantageSystem component - OPTIMIZED
export const useVantageSystemState = () => {
  return useSelector((state) => ({
    funds: selectFunds(state),
    strategies: selectStrategies(state),
    trades: selectTrades(state),
    lots: selectLots(state),
    livePrices: selectLivePrices(state),
    markets: selectMarkets(state),
    marketData: selectMarketData(state),
    RawMarketData: selectRawMarketData(state),
    accountingData: selectAccountingData(state),
    isStrategist: selectIsStrategist(state),
  }), shallowEqual);
};

// Optimized hook for PortfolioLayout component
export const usePortfolioLayoutState = () => {
  return useSelector((state) => ({
    strategies: selectStrategies(state),
    threshold: getMarketDataThreshold(state),
    lots: selectLots(state),
    trades: selectTrades(state),
    isStrategist: selectIsStrategist(state),
    marketData: selectMarketData(state),
    RawMarketData: selectRawMarketData(state),
    livePrices: selectLivePrices(state),
  }), shallowEqual);
};

// Optimized hook for PortfolioTable component
export const usePortfolioTableState = () => {
  return useSelector((state) => ({
    funds: selectFunds(state),
    lots: selectLots(state),
    accountingData: selectAccountingData(state),
    marketData: selectMarketData(state),
    signalsPerformance: selectSignalsPerformance(state),
    markets: selectMarkets(state),
    livePrices: selectLivePrices(state),
    isStrategist: selectIsStrategist(state),
    managers: selectManagers(state),
    strategies: selectStrategies(state),
    trades: selectTrades(state),
  }), shallowEqual);
};

// Optimized hook for FundsBlacklist component
export const useFundsBlacklistState = () => {
  return useSelector((state) => ({
    funds: selectFunds(state),
    strategies: selectStrategies(state),
    managers: selectManagers(state),
  }), shallowEqual);
};


//Optimized hook for Positions component
export const usePositionsState = () => {
  return useSelector((state) => ({
    funds: selectFunds(state),
    lots: selectLots(state),
    strategies: selectStrategies(state),
    custodians: selectCustodians(state),
    accountingData: selectAccountingData(state),
    managers: selectManagers(state),
  }), shallowEqual);
};

//Optimized hook for TradeBlotter component
export const useTradeBlotterState = () => {
  return useSelector((state) => ({
    trades: selectTrades(state),
    deletes: selectDeletes(state),
    lots: selectLots(state),
    accountingData: selectAccountingData(state),
    strategies: selectStrategies(state),
    brokers: selectBrokers(state),
    funds: selectFunds(state),
    custodians: selectCustodians(state),
    routes: selectRoutes(state),
    managers: selectManagers(state),
  }), shallowEqual);
};

//Optimized hook for FundAllocator component
export const useFundAllocatorState = () => {
  return useSelector((state) => ({
    funds: selectFunds(state),
    strategies: selectStrategies(state),
    accountingData: selectAccountingData(state),
    marketData: selectMarketData(state),
    livePrices: selectLivePrices(state),
    markets: selectMarkets(state),
    managers: selectManagers(state),
    brokers: selectBrokers(state),
    lots: selectLots(state),
  }), shallowEqual);
};

//Optimized hook for Signals component
export const useSignalsState = () => {
  return useSelector((state) => ({
    strategies: selectStrategies(state),
    historicalSignalSet: selectHistoricalSignalSet(state),
    blacklistSignal: selectBlacklistSignal(state),
    isStrategist: selectIsStrategist(state),  
  }), shallowEqual);
};

//Optimized hook for Accounting component
export const useAccountingState = () => {
  return useSelector((state) => ({
    accountingData: selectAccountingData(state),
    funds: selectFunds(state),
    strategies: selectStrategies(state),
    managers: selectManagers(state),
  }), shallowEqual);
};

//Optimized hook for Rebalancer component
export const useRebalancerState = () => {
  return useSelector((state) => ({
    livePrices: selectLivePrices(state),
    markets: selectMarkets(state),
    accountingData: selectAccountingData(state),
    isStrategist: selectIsStrategist(state),
    isInvestor: selectIsInvestor(state),
    lots: selectLots(state),
    managers: selectManagers(state),
    marketData: selectMarketData(state),
    AllTrades: selectAllTrades(state),
    blacklistedTickers: selectBlacklistedTickers(state),
    trades: selectTrades(state),
    funds: selectFunds(state),
    strategies: selectStrategies(state),
    brokers: selectBrokers(state),
    custodians: selectCustodians(state),
    routes: selectRoutes(state),
    swapLoading: selectSwapLoading(state),
    swapStrategies: selectSwapStrategies(state),
    swapStrategiesAvailableDate: selectSwapStrategiesAvailableDate(state),
  }), shallowEqual);
};
